import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Element } from 'platform/utilities/scroll';
import { isMinimalHeaderApp } from 'platform/forms-system/src/js/patterns/minimal-header';

import classNames from 'classnames';
import get from '../../../../utilities/data/get';
import set from '../../../../utilities/data/set';
import {
  ERROR_ELEMENTS,
  SCROLL_ELEMENT_SUFFIX,
} from '../../../../utilities/constants';
import { focusReview } from '../utilities/ui/focus-review';

import ProgressButton from '../components/ProgressButton';
import { focusOnChange, fixSelector } from '../utilities/ui';
import SchemaForm from '../components/SchemaForm';
import { showReviewField } from '../helpers';
import {
  getReviewChapterTitle,
  getReviewPageTitle,
  getSchemaPageContent,
  shouldHideExpandedPageTitle,
} from './reviewPageContent';
import ArrayField from './ArrayField';
import { getPreviousPagePath, checkValidPagePath } from '../routing';

import { isValidForm } from '../validation';
import { reduceErrors } from '../utilities/data/reduceErrors';
import { setFormErrors } from '../actions';
import { getPageKey } from '../utilities/review';

/*
 * Displays all the pages in a chapter on the review page
 */
class ReviewCollapsibleChapter extends React.Component {
  constructor(props) {
    super(props);
    this.handleEdit = this.handleEdit.bind(this);
  }

  handleEdit(key, editing, index = null) {
    if (editing || !this.hasValidationError(key, index)) {
      this.props.onEdit(key, editing, index);
      focusReview(
        fixSelector(`${key}${index ?? ''}`),
        editing,
        this.props.reviewEditFocusOnHeaders ?? true,
      );
    }
  }

  onChange(formData, path = null, index = null) {
    let newData = formData;
    if (path) {
      newData = set([path, index], formData, this.props.form.data);
    }
    this.props.setData(newData);
  }

  handleSubmit = (formData, key, path = null, index = null) => {
    // recheck _all_ validations after the user clicks the
    // update page button - needed to dynamically update
    // accordion headers
    const hasError = this.hasValidationError(key, index);

    // This makes sure defaulted data on a page with no changes is saved
    // Probably safe to do this for regular pages, too, but it hasn’t been necessary
    if (path) {
      const newData = set([path, index], formData, this.props.form.data);
      this.props.setData(newData);
    }

    if (!hasError) {
      this.handleEdit(key, false, index);
    }
  };

  goToPath = (customPath, options = {}) => {
    const { form, pageList, location } = this.props;
    const { force, chapterKey } = options;

    const path =
      customPath &&
      (force || checkValidPagePath(pageList, this.props.form.data, customPath))
        ? customPath
        : getPreviousPagePath(pageList, form.data, location.pathname);

    const { pathname, search } = new URL(path, window.location.origin);
    this.props.router.push({
      pathname,
      search,
      state: { chapterKey },
    });
  };

  hasValidationError = (key, index) => {
    const { form, pageList, reviewErrors = {}, formOptions } = this.props;
    const scrollElementKey = `${key}${index ?? ''}`;

    // Update form errors & rawErrors in redux state
    const { errors } = isValidForm(form, pageList, false, formOptions);
    const formData = form.data;
    const cleanedErrors = reduceErrors(
      errors,
      pageList,
      reviewErrors,
      formData,
    );
    this.props.setFormErrors({
      rawErrors: errors,
      errors: cleanedErrors,
    });

    // check if current page has any errors; reviewErrors override needed for
    // custom pages
    const pageKey =
      reviewErrors?._override?.(key, { pageKey: key, index, formData })
        ?.pageKey || scrollElementKey;
    const hasErrors = cleanedErrors.some(error => {
      const errorPageKey =
        reviewErrors?._override?.(error.pageKey, { ...error, formData })
          ?.pageKey || getPageKey(error);
      return errorPageKey === pageKey;
    });

    if (hasErrors) {
      // Focus on first error message on page
      focusOnChange(scrollElementKey, ERROR_ELEMENTS.join(','));
    } else {
      // no error, focus on page edit button after page switches back to review
      // mode
      focusOnChange(scrollElementKey, 'va-button', 'button');
    }
    return hasErrors;
  };

  getChapterTitle = chapterFormConfig =>
    getReviewChapterTitle(chapterFormConfig, this.props.form);

  getPageTitle = rawPageTitle =>
    getReviewPageTitle(rawPageTitle, this.props.form.data);

  getSchemaformPageContent = (page, props, editing) => {
    const { chapterFormConfig, expandedPages, form, formContext, viewedPages } =
      props;

    const pageState = form.pages[page.pageKey];
    const fullPageKey = `${page.pageKey}${page.index ?? ''}`;
    const { pageSchema, pageUiSchema, pageData, arrayFields } =
      getSchemaPageContent(page, pageState, form.data);

    const hasError = props.form.formErrors?.errors?.some(
      err => fullPageKey === getPageKey(err),
    );

    const classes = classNames('form-review-panel-page', {
      'schemaform-review-page-error': hasError || !viewedPages.has(fullPageKey),
      // Remove bottom margin when the div content is empty
      'vads-u-margin-bottom--0': !pageSchema && arrayFields.length === 0,
    });

    const title = page.reviewTitle || page.title || '';
    const labelTitle = pageUiSchema['ui:options']?.itemAriaLabel || title;
    const ariaText =
      typeof labelTitle === 'function' ? labelTitle(pageData) : labelTitle;
    const ariaLabel = `Update ${ariaText || 'page'}`;
    const formOptions = this.props.formOptions || {};

    const visibleFields =
      pageSchema &&
      Object.entries(pageSchema.properties).filter(([propName]) =>
        showReviewField(
          propName,
          pageSchema,
          pageUiSchema,
          form.data,
          formContext,
        ),
      ).length > 0;

    if (!visibleFields) {
      return null;
    }

    return (
      <div key={`${fullPageKey}`} className={classes}>
        <Element name={`${fullPageKey}${SCROLL_ELEMENT_SUFFIX}`} />
        <SchemaForm
          name={page.pageKey}
          title={title}
          data={pageData}
          appStateData={page.appStateData}
          schema={pageSchema}
          uiSchema={pageUiSchema}
          trackingPrefix={this.props.form.trackingPrefix}
          hideHeaderRow={page.hideHeaderRow}
          hideTitle={shouldHideExpandedPageTitle(
            expandedPages,
            this.getChapterTitle(chapterFormConfig),
            this.getPageTitle(title),
          )}
          pagePerItemIndex={page.index}
          onBlur={this.props.onBlur}
          onEdit={() => this.handleEdit(page.pageKey, !editing, page.index)}
          onSubmit={({ formData }) =>
            this.handleSubmit(
              formData,
              page.pageKey,
              page.arrayPath,
              page.index,
            )
          }
          onChange={formData =>
            this.onChange(
              typeof page.updateFormData === 'function'
                ? page.updateFormData(form.data, formData, page.index)
                : formData,
              page.arrayPath,
              page.index,
            )
          }
          uploadFile={this.props.uploadFile}
          reviewMode={!editing}
          formContext={{ ...formContext, formOptions }}
          formOptions={formOptions}
          editModeOnReviewPage={page.editModeOnReviewPage}
        >
          {!editing ? (
            <div />
          ) : (
            <ProgressButton
              submitButton
              onButtonClick={() => {
                // recheck _all_ validations after the user clicks the
                // update page button - needed to dynamically update
                // accordion headers
                if (!this.hasValidationError(page.pageKey, page.index)) {
                  focusOnChange(getPageKey(page), 'va-button', 'button');
                }
              }}
              buttonText="Update"
              buttonClass="usa-button-primary"
              ariaLabel={ariaLabel}
              useWebComponents={
                this.props.formOptions?.useWebComponentForNavigation
              }
            />
          )}
        </SchemaForm>
        {arrayFields.map(arrayField => (
          <div key={arrayField.path} className="form-review-array">
            <ArrayField
              pageKey={page.pageKey}
              pageTitle={page.title}
              arrayData={get(arrayField.path, form.data)}
              formData={form.data}
              appStateData={page.appStateData}
              formContext={{ ...formContext, formOptions }}
              pageConfig={page}
              onBlur={this.props.onBlur}
              schema={arrayField.schema}
              uiSchema={arrayField.uiSchema}
              trackingPrefix={this.props.form.trackingPrefix}
              setData={formData =>
                this.props.setData(
                  typeof page.updateFormData === 'function'
                    ? page.updateFormData(form.data, formData, page.index)
                    : formData,
                )
              }
              path={arrayField.path}
            />
          </div>
        ))}
      </div>
    );
  };

  getCustomPageContent = (page, props, editing) => {
    const pageState = props.form.pages[page.pageKey];
    const { pageSchema, pageUiSchema } = getSchemaPageContent(
      page,
      pageState,
      props.form.data,
    );

    const fullPageKey = getPageKey(page);

    if (editing) {
      // noop defined as a function for unit tests
      const noop = function noop() {};

      const pageTitle = this.getPageTitle(page.title);
      const updatePageAriaLabel = pageTitle
        ? `Update ${pageTitle}`
        : 'Update page';

      return (
        <React.Fragment key={fullPageKey}>
          <Element name={`${fullPageKey}${SCROLL_ELEMENT_SUFFIX}`} />
          <div>
            <page.CustomPage
              name={page.pageKey}
              chapterKey={this.props.chapterKey}
              title={page.title}
              trackingPrefix={props.form.trackingPrefix}
              uploadFile={props.uploadFile}
              onReviewPage
              setFormData={props.setData}
              data={props.form.data}
              updatePage={() =>
                this.handleEdit(page.pageKey, false, page.index)
              }
              updatePageAriaLabel={updatePageAriaLabel}
              recalculateErrors={this.hasValidationError}
              pagePerItemIndex={page.index}
              schema={pageSchema}
              uiSchema={pageUiSchema}
              // noop for navigation to prevent JS error
              goBack={noop}
              goForward={noop}
              goToPath={this.goToPath}
              urlPrefix={this.props.urlPrefix || ''}
            />
          </div>
        </React.Fragment>
      );
    }

    const hasError = props.form.formErrors?.errors?.some(
      err => fullPageKey === getPageKey(err),
    );

    const classes = classNames('form-review-panel-page', {
      'schemaform-review-page-error':
        hasError || !props.viewedPages.has(fullPageKey),
    });

    return (
      <div key={`${fullPageKey}`} className={classes}>
        <Element name={`${fullPageKey}${SCROLL_ELEMENT_SUFFIX}`} />
        <div>
          <page.CustomPageReview
            key={`${page.pageKey}Review${page.index ?? ''}`}
            chapterKey={this.props.chapterKey}
            editPage={() => this.handleEdit(page.pageKey, !editing, page.index)}
            name={page.pageKey}
            title={page.title}
            data={props.form.data}
            fullData={props.form.data}
            setFormData={props.setData}
            pagePerItemIndex={page.index}
            goToPath={this.goToPath}
            recalculateErrors={this.hasValidationError}
            urlPrefix={this.props.urlPrefix || ''}
          />
        </div>
      </div>
    );
  };

  getChapterContent = props => {
    const { chapterFormConfig, expandedPages, form, pageKeys, viewedPages } =
      props;
    const ChapterDescription = chapterFormConfig.reviewDescription;
    return (
      <div data-testid="accordion-item-content">
        {ChapterDescription && (
          <ChapterDescription
            viewedPages={viewedPages}
            pageKeys={pageKeys}
            formData={form.data}
            t
            push
          />
        )}
        {expandedPages?.map(page => {
          const pageConfig = form.pages[page.pageKey];
          const editing = pageConfig.showPagePerItem
            ? pageConfig.editMode[page.index]
            : pageConfig.editMode;

          const showCustomPage = editing
            ? !!pageConfig.CustomPage
            : !!pageConfig.CustomPageReview;

          return showCustomPage
            ? this.getCustomPageContent(page, props, editing)
            : this.getSchemaformPageContent(page, props, editing);
        }) ?? null}
      </div>
    );
  };

  render() {
    const chapterTitle = this.getChapterTitle(this.props.chapterFormConfig);
    const subHeader = chapterTitle
      ? `Error: There may be errors with ${chapterTitle}. Review your information before continuing.`
      : 'Error: There may be errors with this section. Review your information before continuing.';

    return (
      <>
        <Element
          name={`chapter${this.props.chapterKey}${SCROLL_ELEMENT_SUFFIX}`}
        />
        <va-accordion-item
          data-chapter={this.props.chapterKey}
          subHeader={this.props.hasUnviewedPages ? subHeader : ''}
          data-unviewed-pages={this.props.hasUnviewedPages}
          open={this.props.open}
          bordered
          header={chapterTitle}
          level={isMinimalHeaderApp() ? 2 : 3}
        >
          {this.props.hasUnviewedPages && (
            <va-icon slot="icon" icon="error" class="vads-u-color--secondary" />
          )}
          {this.getChapterContent(this.props)}
        </va-accordion-item>
      </>
    );
  }
}

const mapStateToProps = state => state;

const mapDispatchToProps = {
  setFormErrors,
};

// TODO: refactor to pass form.data instead of the entire form object
ReviewCollapsibleChapter.propTypes = {
  chapterFormConfig: PropTypes.object.isRequired,
  chapterKey: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  hasUnviewedPages: PropTypes.bool.isRequired,
  pageList: PropTypes.array.isRequired,
  setData: PropTypes.func.isRequired,
  setFormErrors: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  formOptions: PropTypes.shape({
    useWebComponentForNavigation: PropTypes.bool,
    validateWhiteSpace: PropTypes.bool,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  open: PropTypes.bool,
  reviewEditFocusOnHeaders: PropTypes.bool,
  reviewErrors: PropTypes.shape({}),
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
  uploadFile: PropTypes.func,
  urlPrefix: PropTypes.string,
  onBlur: PropTypes.func,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ReviewCollapsibleChapter),
);

// for tests
export { ReviewCollapsibleChapter };
